import { apiGetCustomers } from "@lib/api/customer";
import { apiGetProviders } from "@lib/api/provider";
import CustomerName from "@lib/components/MiniComponents/Others/CustomerName";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { UseMutationProps } from "@lib/types/Api";
import { AuthRoles } from "@lib/types/Auth";
import { Manager } from "@lib/types/Manager";
import { useQuery } from "@tanstack/react-query";
import { Button, Form, Input, Modal, Select, Space, Switch } from "antd";
import { useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import { VscSparkleFilled } from "react-icons/vsc";
import { generateRandomPassword } from "@lib/helpers/StringHelper";
import ProviderName from "@lib/components/MiniComponents/Others/ProviderName";
import PhoneCountryCodes from "@lib/components/MiniComponents/FormItems/PhoneCountryCodes";
import { getRoleOptionsByContext } from "@lib/helpers/RoleHelper";
import { useTypedSelector } from "@lib/redux";
import { apiGetCustomer } from "@lib/api/customer";
import { apiGetProvider } from "@lib/api/provider";
import { managerSchema as yupSchema } from "@lib/schemas/manager";
import { createYupSync } from "@lib/helpers/TableHelper";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { AppContext } from "@lib/types";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

type Props = {
  openModal: boolean;
  toggleModal: (status?: boolean) => void;
  mutationQuery: UseMutationProps<Partial<Manager>>;
  manager?: Manager;
  context?: AppContext;
  preSelectedProvider?: string;
  preSelectedCustomer?: string;
};

export default function ManagerModal({
  openModal,
  toggleModal,
  mutationQuery,
  manager,
  context,
  preSelectedProvider,
  preSelectedCustomer,
}: Props) {
  const { messages, formatMessage } = useIntl();
  const [form] = Form.useForm();
  const { mutate, isPending } = mutationQuery;
  const { user } = useTypedSelector((state) => state.auth);
  const userRole = user?.role;
  const ipWhitelist = Form.useWatch("ipWhitelist", form);

  const [phoneCode, setPhoneCode] = useState("");

  const getDefaultRole = () => {
    switch (context) {
      case AppContext.CUSTOMER:
        return AuthRoles.CustomerAgent;
      case AppContext.PAYMENT_PROVIDER:
        return AuthRoles.ProviderAgent;
      default:
        return null;
    }
  };

  const initialValues: Partial<Manager> = {
    isTwoFactorAuthEnabled: false,
    role: getDefaultRole(),
    providerId: preSelectedProvider,
    customerId: preSelectedCustomer,
    ipWhitelist: null,
  } as const;

  useEffect(() => {
    if (openModal) {
      form?.setFieldsValue(manager || initialValues);
    }
  }, [openModal, manager]);

  const role = Form.useWatch("role", form);

  const { data: customers, isLoading: customersIsLoading } = useQuery({
    queryFn: () => apiGetCustomers({ limit: 100 }),
    queryKey: ["customers-dropdown"],
    enabled:
      (role == AuthRoles.CustomerAgent || role == AuthRoles.CustomerManager) &&
      userRole === AuthRoles.Admin,
  });

  const { data: providers, isLoading: providersIsLoading } = useQuery({
    queryFn: () => apiGetProviders({ limit: 100 }),
    queryKey: ["providers-dropdown"],
    enabled: role == AuthRoles.ProviderAgent && userRole === AuthRoles.Admin,
  });

  // Pre-selected customer query
  const { data: preSelectedCustomerData } = useQuery({
    queryFn: () => apiGetCustomer(preSelectedCustomer),
    queryKey: ["customer", preSelectedCustomer],
    enabled: !!preSelectedCustomer,
  });

  // Pre-selected provider query
  const { data: preSelectedProviderData } = useQuery({
    queryFn: () => apiGetProvider(preSelectedProvider),
    queryKey: ["provider", preSelectedProvider],
    enabled: !!preSelectedProvider,
  });

  const handleResetForm = useCallback(() => {
    form?.setFieldsValue(initialValues);
  }, []);

  const handleClose = useCallback(() => {
    toggleModal(false);
    handleResetForm();
  }, [toggleModal, form]);

  return (
    <Modal
      title={
        <FormattedMessage
          id={manager ? "common.editTitle" : "common.createTitle"}
          values={{ title: messages["common.manager"] as string }}
        />
      }
      open={openModal}
      onCancel={handleClose}
      footer={null}
      confirmLoading={isPending}
    >
      <Form<Manager>
        {...layout}
        form={form}
        name={"manager-modal"}
        onFinish={(values) => mutate({ phoneCode, ...values, id: manager?.id })}
        style={{ maxWidth: 600, marginTop: 20 }}
        autoComplete="off"
        initialValues={{
          isTwoFactorAuthEnabled: false,

          ...manager,
        }}
      >
        <Form.Item
          name="firstName"
          label={messages["common.firstName"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <Input
            placeholder={formatMessage(
              {
                id: "helper.pleaseTypeX",
              },
              { x: messages["common.firstName"] as string },
            )}
          />
        </Form.Item>
        <Form.Item
          name="lastName"
          label={messages["common.lastName"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <Input
            placeholder={formatMessage(
              {
                id: "helper.pleaseTypeX",
              },
              { x: messages["common.lastName"] as string },
            )}
          />
        </Form.Item>

        <Form.Item
          name="email"
          label={messages["common.email"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
          // required={}
        >
          <Input
            placeholder={formatMessage(
              {
                id: "helper.pleaseTypeX",
              },
              { x: messages["common.email"] as string },
            )}
          />
        </Form.Item>

        <Form.Item
          label={messages["common.password"] as string}
          name="password"
          rules={[{ required: !manager }]}
        >
          <Input.Password
            addonAfter={
              <ActionButton
                type="other"
                noLabel
                noBg
                size="small"
                icon={<VscSparkleFilled />}
                label={messages["common.generateRandomPassword"] as string}
                onClick={() =>
                  form?.setFieldValue("password", generateRandomPassword(15))
                }
              />
            }
            placeholder={formatMessage(
              {
                id: "helper.pleaseTypeX",
              },
              { x: messages["common.password"] as string },
            )}
          />
        </Form.Item>

        <Form.Item
          name="phone"
          label={messages["common.phone"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <Input
            addonBefore={
              <PhoneCountryCodes
                onChange={(value) => {
                  setPhoneCode(value);
                }}
                placeholder={"*"}
              />
            }
            placeholder={formatMessage(
              {
                id: "helper.pleaseTypeX",
              },
              { x: messages["common.phone"] as string },
            )}
          />
        </Form.Item>

        <Form.Item
          name="isTwoFactorAuthEnabled"
          label={messages["common.mfa"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <Switch
            checkedChildren={messages["common.enabled"] as string}
            unCheckedChildren={messages["common.disabled"] as string}
          />
        </Form.Item>

        <Form.Item
          name="ipWhitelist"
          label={messages["common.ipWhitelist"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
          // help={messages["helper.ipWhitelistHelp"] as string}
        >
          <Select
            mode="tags"
            tokenSeparators={[","]}
            placeholder={formatMessage(
              {
                id: "helper.pleaseTypeX",
              },
              { x: messages["common.ipWhitelist"] as string },
            )}
            allowClear
            value={ipWhitelist}
            onChange={(value) => {
              form?.setFieldValue("ipWhitelist", value);
            }}
          />
        </Form.Item>

        <Form.Item
          name="role"
          label={messages["common.role"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <Select
            placeholder="Select manager role"
            allowClear
            showSearch
            optionFilterProp="text"
            options={getRoleOptionsByContext(
              user?.role || AuthRoles.Admin,
              context,
            )}
            disabled={Boolean(
              (preSelectedProvider && preSelectedProviderData?.data) ||
                (preSelectedCustomer && preSelectedCustomerData?.data),
            )}
          />
        </Form.Item>

        {userRole === AuthRoles.Admin &&
          (role === AuthRoles.CustomerAgent ||
            role === AuthRoles.CustomerManager) && (
            <>
              {preSelectedCustomer && preSelectedCustomerData?.data ? (
                <Form.Item
                  name="customerId"
                  label={messages["common.customer"] as string}
                  rules={[createYupSync({ schema: yupSchema })]}
                >
                  <Input
                    value={preSelectedCustomerData.data.name}
                    disabled
                    addonBefore={
                      <CustomerName customer={preSelectedCustomerData.data} />
                    }
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  name="customerId"
                  label={messages["common.customer"] as string}
                  rules={[createYupSync({ schema: yupSchema })]}
                >
                  <Select
                    placeholder={formatMessage(
                      {
                        id: "helper.pleaseSelectToX",
                      },
                      { x: messages["common.customer"] as string },
                    )}
                    allowClear
                    showSearch
                    loading={customersIsLoading}
                    optionFilterProp="text"
                    options={customers?.data?.data?.map((customer) => ({
                      label: <CustomerName customer={customer} />,
                      text: customer.name,
                      value: customer.id,
                    }))}
                  />
                </Form.Item>
              )}
            </>
          )}

        {userRole === AuthRoles.Admin && role === AuthRoles.ProviderAgent && (
          <>
            {preSelectedProvider && preSelectedProviderData?.data ? (
              <Form.Item
                name="providerId"
                label={messages["common.provider"] as string}
                rules={[createYupSync({ schema: yupSchema })]}
              >
                <Input
                  value={preSelectedProviderData.data.name}
                  disabled
                  addonBefore={
                    <ProviderName provider={preSelectedProviderData.data} />
                  }
                />
              </Form.Item>
            ) : (
              <Form.Item
                name="providerId"
                label={messages["common.provider"] as string}
                rules={[createYupSync({ schema: yupSchema })]}
              >
                <Select
                  allowClear
                  showSearch
                  loading={providersIsLoading}
                  optionFilterProp="text"
                  options={providers?.data?.data?.map((provider) => ({
                    label: <ProviderName provider={provider} />,
                    text: provider.name,
                    value: provider.id,
                  }))}
                  placeholder={formatMessage(
                    {
                      id: "helper.pleaseSelectToX",
                    },
                    { x: messages["common.provider"] as string },
                  )}
                />
              </Form.Item>
            )}
          </>
        )}

        {userRole === AuthRoles.Admin && role === AuthRoles.Admin && (
          <Form.Item
            name="isManagement"
            label={messages["common.isManagement"] as string}
            rules={[createYupSync({ schema: yupSchema })]}
          >
            <Switch
              checkedChildren={messages["common.enabled"] as string}
              unCheckedChildren={messages["common.disabled"] as string}
            />
          </Form.Item>
        )}

        <StyledFlex style={{ justifyContent: "end" }}>
          <Space>
            <Button
              htmlType="button"
              onClick={handleClose}
              disabled={isPending}
            >
              {messages["common.reset"] as string}
            </Button>
            <Button type="primary" htmlType="submit" loading={isPending}>
              {manager?.id
                ? (messages["common.update"] as string)
                : (messages["common.create"] as string)}
            </Button>
          </Space>
        </StyledFlex>
      </Form>
    </Modal>
  );
}
